import { useState, useRef } from 'react';
import {
  DrawerRoot,
  DrawerContent,
  DrawerHeader,
  DrawerBody,
  DrawerTitle,
  DrawerCloseTrigger,
} from '@/components/ui/drawer';
import { Button, Flex } from '@chakra-ui/react';
import { FaEdit, FaSave, FaTimes } from 'react-icons/fa';
import { dataTableButtonStyles } from '@/components/data-table';
import { useGetLocation } from '../locations.hook';
import { LocationEditForm } from './location-edit-form';

interface LocationDetailsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  locationId: string | null | undefined;
}

export const LocationDetailsDrawer = ({
  isOpen,
  onClose,
  locationId,
}: LocationDetailsDrawerProps) => {
  const [drawerMode, setDrawerMode] = useState<'view' | 'edit'>('view');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formRef = useRef<{ submit: () => void; reset: () => void } | null>(
    null,
  );

  const { data: location } = useGetLocation(locationId);

  const handleSuccess = () => {
    setDrawerMode('view');
    setIsSubmitting(false);
    // The drawer will automatically refresh due to React Query cache invalidation
  };

  const handleCancel = () => {
    formRef.current?.reset();
    setDrawerMode('view');
  };

  const handleEdit = () => {
    setDrawerMode('edit');
  };

  const handleSave = () => {
    formRef.current?.submit();
  };

  const handleClose = () => {
    setDrawerMode('view');
    onClose();
  };

  return (
    <DrawerRoot
      open={isOpen}
      onOpenChange={({ open }) => !open && handleClose()}
      size="md"
      placement="end"
      closeOnInteractOutside={false}
    >
      <DrawerContent>
        <DrawerHeader>
          <Flex justify="flex" align="center" gap={2}>
            <DrawerTitle>
              {drawerMode === 'view' ? 'Location Details' : 'Edit Location'}
            </DrawerTitle>
            <Flex gap={2} align="center">
              {drawerMode === 'edit' ? (
                <>
                  <Button
                    {...dataTableButtonStyles.outline}
                    onClick={handleCancel}
                    disabled={isSubmitting}
                    size="sm"
                  >
                    <FaTimes />
                    Cancel
                  </Button>
                  <Button
                    {...dataTableButtonStyles.solid}
                    onClick={handleSave}
                    loading={isSubmitting}
                    disabled={isSubmitting}
                    size="sm"
                  >
                    <FaSave />
                    Save
                  </Button>
                </>
              ) : (
                <Button
                  {...dataTableButtonStyles.outline}
                  onClick={handleEdit}
                  size="sm"
                >
                  <FaEdit />
                  Edit
                </Button>
              )}
              <DrawerCloseTrigger />
            </Flex>
          </Flex>
        </DrawerHeader>

        <DrawerBody>
          {location && (
            <LocationEditForm
              ref={formRef}
              location={location}
              onSuccess={handleSuccess}
              isViewMode={drawerMode === 'view'}
              onSubmittingChange={setIsSubmitting}
            />
          )}
        </DrawerBody>
      </DrawerContent>
    </DrawerRoot>
  );
};
